import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const EnhancedBreadcrumb = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Enhanced breadcrumb mapping with proper hierarchy
  const breadcrumbMap = {
    '/dashboard': [
      { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' }
    ],

    '/crm': [
      { label: 'CRM Pipeline', path: '/crm', icon: 'GitBranch' }
    ],
    '/clients': [
      { label: 'Client Management', path: '/clients', icon: 'Users' }
    ],
    '/campaigns': [
      { label: 'Campaigns', path: '/campaigns', icon: 'Megaphone' }
    ],
    '/properties': [
      { label: 'Properties', path: '/properties', icon: 'Home' }
    ],
    '/reviews': [
      { label: 'Reviews', path: '/reviews', icon: 'Star' }
    ],
    '/instant-request': [
      { label: 'Reviews', path: '/reviews', icon: 'Star' },
      { label: 'Instant Request', path: '/instant-request', icon: 'Zap' }
    ],
    '/services': [
      { label: 'Business Management', path: null, icon: 'Building' },
      { label: 'Service Catalog', path: '/services', icon: 'List' }
    ],
    '/reports-analytics': [
      { label: 'Analytics & Reports', path: null, icon: 'BarChart3' },
      { label: 'Performance Dashboard', path: '/reports-analytics', icon: 'TrendingUp' }
    ],
    '/reports/financial': [
      { label: 'Analytics & Reports', path: null, icon: 'BarChart3' },
      { label: 'Financial Reports', path: '/reports/financial', icon: 'DollarSign' }
    ],
    '/reports/customers': [
      { label: 'Analytics & Reports', path: null, icon: 'BarChart3' },
      { label: 'Customer Analytics', path: '/reports/customers', icon: 'Users' }
    ],
    '/settings': [
      { label: 'Settings', path: null, icon: 'Settings' },
      { label: 'Settings', path: '/settings', icon: 'Building' }
    ],
    '/settings/users': [
      { label: 'Settings', path: null, icon: 'Settings' },
      { label: 'User Management', path: '/settings/users', icon: 'UserCog' }
    ],
    '/settings/system': [
      { label: 'Settings', path: null, icon: 'Settings' },
      { label: 'System Config', path: '/settings/system', icon: 'Settings' }
    ],
    '/settings/integrations': [
      { label: 'Settings', path: null, icon: 'Settings' },
      { label: 'Integrations', path: '/settings/integrations', icon: 'Plug' }
    ],
    '/reviews/submit': [
      { label: 'Customer Review', path: '/reviews/submit', icon: 'Star' }
    ]
  };

  const currentPath = location.pathname;
  const breadcrumbs = breadcrumbMap[currentPath] || [
    { label: 'Page Not Found', path: currentPath, icon: 'AlertCircle' }
  ];

  const handleNavigate = (path) => {
    if (path) {
      navigate(path);
    }
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="bg-surface border-b border-border px-6 py-3">
      <div className="flex items-center justify-between">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center space-x-2 text-sm">
          {/* Home Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-text-muted hover:text-text-primary px-2"
          >
            <Icon name="Home" size={16} />
          </Button>

          {/* Breadcrumb Items */}
          {breadcrumbs.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && (
                <Icon name="ChevronRight" size={14} className="text-text-muted" />
              )}
              
              <div className="flex items-center space-x-2">
                <Icon 
                  name={item.icon} 
                  size={14} 
                  className={`${
                    index === breadcrumbs.length - 1 
                      ? 'text-primary' 
                      : 'text-text-muted'
                  }`} 
                />
                
                {item.path && index < breadcrumbs.length - 1 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleNavigate(item.path)}
                    className="text-text-muted hover:text-text-primary px-1 py-0 h-auto font-medium"
                  >
                    {item.label}
                  </Button>
                ) : (
                  <span 
                    className={`font-medium ${
                      index === breadcrumbs.length - 1 
                        ? 'text-text-primary' 
                        : 'text-text-muted'
                    }`}
                  >
                    {item.label}
                  </span>
                )}
              </div>
            </React.Fragment>
          ))}
        </nav>

        {/* Navigation Actions */}
        <div className="flex items-center space-x-2">
          {/* Back Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleGoBack}
            className="text-text-muted hover:text-text-primary"
          >
            <Icon name="ArrowLeft" size={16} className="mr-1" />
            Back
          </Button>

          {/* Refresh Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.location.reload()}
            className="text-text-muted hover:text-text-primary"
          >
            <Icon name="RefreshCw" size={16} />
          </Button>
        </div>
      </div>

      {/* Page Context Info */}
      {breadcrumbs.length > 1 && (
        <div className="mt-2 flex items-center space-x-4 text-xs text-text-muted">
          <span className="flex items-center space-x-1">
            <Icon name="Clock" size={12} />
            <span>Last updated: {new Date().toLocaleTimeString()}</span>
          </span>
          
          {/* Current Section Info */}
          {breadcrumbs.length > 1 && (
            <span className="flex items-center space-x-1">
              <Icon name="Layers" size={12} />
              <span>{breadcrumbs[0].label} Section</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedBreadcrumb;